import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";

/**
 * NumberInput — a controlled numeric `<input type="number">` that lets the
 * user actually edit. The default `parseInt(e.target.value || "10", 10) || 10`
 * pattern snaps the field back to its default the moment you erase a digit,
 * so you can never type a smaller number. This component fixes that:
 *
 *  * keeps a local string buffer so empty / partial input is allowed mid-edit,
 *  * commits a parsed integer to the parent only when the user types a valid
 *    number,
 *  * clamps to [min, max] on blur (or on form submit if you call `commit()`).
 *
 * Props:
 *   value       number|null — the current persisted value
 *   onChange    (n: number|null) => void
 *   min         number (default 0)
 *   max         number (default 1_000_000)
 *   step        number (default 1)
 *   fallback    number — what to use when the field is left empty on blur
 *               (only consulted when allowEmpty is false)
 *   allowEmpty  boolean — when true, an empty field stays empty and onChange
 *               is called with null. When false (default), blur snaps to
 *               `fallback` (or `min`).
 *
 * All other props (placeholder, data-testid, className, disabled, …) are
 * forwarded to the underlying Input.
 */
export function NumberInput({
  value,
  onChange,
  min = 0,
  max = 1_000_000,
  step = 1,
  fallback,
  allowEmpty = false,
  ...rest
}) {
  const [raw, setRaw] = useState(
    value === null || value === undefined ? "" : String(value),
  );
  // Keep the buffer in sync when the parent value changes externally
  // (e.g. settings reload). We only override the buffer if the parent
  // value actually represents a different number than what's in the
  // buffer, so we don't clobber what the user is currently typing.
  useEffect(() => {
    const parsed = parseInt(raw, 10);
    const numericRaw = Number.isFinite(parsed) ? parsed : null;
    const incoming = value === null || value === undefined ? null : Number(value);
    if (numericRaw !== incoming) {
      setRaw(incoming === null ? "" : String(incoming));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e) => {
    const v = e.target.value;
    // Allow empty + digits only (negative not supported here; min defaults
    // to 0 and the only consumers of this component are non-negative).
    if (v === "" || /^\d+$/.test(v)) {
      setRaw(v);
      if (v === "") {
        if (allowEmpty) {
          onChange(null);
        }
        // Don't commit on empty when not allowed — let the user keep typing.
        return;
      }
      const n = parseInt(v, 10);
      if (Number.isFinite(n)) {
        onChange(n);
      }
    }
  };

  const handleBlur = () => {
    if (raw === "") {
      if (allowEmpty) {
        // Keep empty; parent already got null on first clear.
        return;
      }
      const fb = fallback !== undefined ? fallback : min;
      setRaw(String(fb));
      onChange(fb);
      return;
    }
    let n = parseInt(raw, 10);
    if (!Number.isFinite(n)) {
      if (allowEmpty) {
        setRaw("");
        onChange(null);
        return;
      }
      const fb = fallback !== undefined ? fallback : min;
      setRaw(String(fb));
      onChange(fb);
      return;
    }
    if (n < min) n = min;
    if (n > max) n = max;
    setRaw(String(n));
    onChange(n);
  };

  return (
    <Input
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      step={step}
      value={raw}
      onChange={handleChange}
      onBlur={handleBlur}
      {...rest}
    />
  );
}
